import { router } from '@/configs/routes';
import { routerUrls } from '@/configs/routes/routerUrls';
import { IS_V2_WRITE_ENABLED } from '@/configs/v2Write';
import {
  BatchPublishDraftsResult,
  CreateTimelogRequest,
  Timelog,
  TimelogsTask,
  TimelogsTaskFull,
  TimelogsUser,
  UpdateTimelogRequest,
} from '@/types/timelog';
import { getLegacyLoginUrl } from '@/utils/legacyLoginUrl';

import { createApiInstance } from './createApiInstance';
import { ItemResponse, ItemsResponse } from './types';

const PREFIX = '/api/v2';
const MAX_PAGE_SIZE = 200;
// Длиннее подсказки бэк отклоняет с 400, а по такому хвосту всё равно ничего не найдётся
const MAX_SUGGEST_QUERY_LENGTH = 200;

type PageResponse<T> = { items: T[]; nextPageToken: string | null };

/** Собственный учёт времени (API v2): логи живут в нашей БД, с Яндекс Трекером не синхронизируются */
class TimelogsApi {
  private axiosInstance = createApiInstance('timelogs', {
    readOnly: !IS_V2_WRITE_ENABLED,
    onUnauthorized: () => {
      if (!window.location.pathname.startsWith(routerUrls.login)) {
        router.navigate(getLegacyLoginUrl(window.location.pathname + window.location.search));
      }
    },
  });

  /** Логи пользователя за период, все страницы */
  async searchUserTimelogs({
    userId,
    from,
    to,
    signal,
  }: {
    userId: string;
    from: string;
    to: string;
    signal?: AbortSignal;
  }): Promise<Timelog[]> {
    return this.searchAllTimelogs({
      filters: { user: { eq: userId }, start: { gte: from, lte: to } },
      signal,
    });
  }

  /** Все логи задачи — для статистики «оценка и факт» */
  async searchTaskTimelogs({
    taskKey,
    signal,
  }: {
    taskKey: string;
    signal?: AbortSignal;
  }): Promise<Timelog[]> {
    return this.searchAllTimelogs({ filters: { task: { eq: taskKey } }, signal });
  }

  private async searchAllTimelogs({
    filters,
    signal,
  }: {
    filters: Record<string, unknown>;
    signal?: AbortSignal;
  }): Promise<Timelog[]> {
    const items: Timelog[] = [];
    let pageToken: string | undefined;

    do {
      const response = await this.axiosInstance.post<PageResponse<Timelog>>(
        `${PREFIX}/timelogs:search`,
        {
          filters,
          orderBy: [{ field: 'start', direction: 'asc' }],
          pageSize: MAX_PAGE_SIZE,
          pageToken,
        },
        { signal },
      );

      items.push(...response.data.items);
      pageToken = response.data.nextPageToken ?? undefined;
    } while (pageToken);

    return items;
  }

  /** Все или ничего: при ошибке в любом элементе не создаётся ни один лог */
  async createTimelogs(timelogs: CreateTimelogRequest[]): Promise<Timelog[]> {
    const response = await this.axiosInstance.post<ItemsResponse<Timelog>>(
      `${PREFIX}/timelogs:batchCreate`,
      { timelogs },
    );

    return response.data.items;
  }

  async updateTimelog({ id, data }: { id: string; data: UpdateTimelogRequest }): Promise<Timelog> {
    const response = await this.axiosInstance.patch<ItemResponse<Timelog>>(
      `${PREFIX}/timelogs/${encodeURIComponent(id)}`,
      data,
    );

    return response.data.item;
  }

  async deleteTimelog(id: string): Promise<void> {
    await this.axiosInstance.delete(`${PREFIX}/timelogs/${encodeURIComponent(id)}`);
  }

  /** Черновики общие с v1; публикация сюда создаёт лог в нашей БД, а не в Трекере */
  async publishDraft(id: string): Promise<Timelog> {
    const response = await this.axiosInstance.post<ItemResponse<Timelog>>(
      `${PREFIX}/draftTimelogs/${encodeURIComponent(id)}:publish`,
    );

    return response.data.item;
  }

  async publishDrafts({
    from,
    to,
  }: {
    from: string;
    to: string;
  }): Promise<BatchPublishDraftsResult> {
    const response = await this.axiosInstance.post<BatchPublishDraftsResult>(
      `${PREFIX}/draftTimelogs:batchPublish`,
      { start: { gte: from, lte: to } },
    );

    return response.data;
  }

  async suggestTasks({
    query,
    signal,
  }: {
    query: string;
    signal?: AbortSignal;
  }): Promise<TimelogsTask[]> {
    const response = await this.axiosInstance.post<ItemsResponse<TimelogsTask>>(
      `${PREFIX}/tasks:suggest`,
      { query: query.slice(0, MAX_SUGGEST_QUERY_LENGTH) },
      { signal },
    );

    return response.data.items;
  }

  async getTask(key: string): Promise<TimelogsTaskFull> {
    const response = await this.axiosInstance.get<ItemResponse<TimelogsTaskFull>>(
      `${PREFIX}/tasks/${encodeURIComponent(key)}`,
    );

    return response.data.item;
  }

  async suggestUsers({
    query,
    includeExternal,
    signal,
  }: {
    query: string;
    includeExternal: boolean;
    signal?: AbortSignal;
  }): Promise<TimelogsUser[]> {
    const response = await this.axiosInstance.post<ItemsResponse<TimelogsUser>>(
      `${PREFIX}/users:suggest`,
      { query: query.slice(0, MAX_SUGGEST_QUERY_LENGTH), includeExternal },
      { signal },
    );

    return response.data.items;
  }

  async getUser(id: string): Promise<TimelogsUser> {
    const response = await this.axiosInstance.get<ItemResponse<TimelogsUser>>(
      `${PREFIX}/users/${encodeURIComponent(id)}`,
    );

    return response.data.item;
  }
}

export const timelogsApi = new TimelogsApi();
